import React, { useEffect, useState } from "react";
import { CartState, LnFe } from "../../utils/interfaces/cart";
import Trash from "../../assets/images/trash.svg";
import constants from "./constants.json";
import Field from "./fields";
import { Dialog } from "../../components";
import { StaticAlert } from "../../components";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { selectLocationSelector } from "../../store/locations/selector";
import { selectStoreConfigSelector } from "../../store/storeConfig/selector";
import cartService from "../../services/cartService";
import { cartActionDispatch } from "../../store/cart/actions";
import { addDataLayer, appendUtmParamsToLinks } from "../../utils/utility";
import {
  fieldValidations,
  checkForDuplicateEmails,
  validateAddress,
  validateEmail,
} from "../../utils/validations";
import Button from "../../components/button";
import { cartValueEventDL } from "../../utils/dataLayerManager";

interface UserDetailsFormProps {
  cartData: CartState;
  userData?: Array<LnFe>;
  maxAttornies: number;
  setAdminForm: (res: boolean) => void;
  storeCode?: string;
  addressValidationURL?: string;
  requireValidation: string;
}

const UserDetailsForm = (props: UserDetailsFormProps) => {
  const generateKeyFromCart = (type: string = "form") => {
    const adminFormObject = constants.formSection.formFields.reduce(
      (acc, key) => ({ ...acc, [key.id]: "" }),
      {} as any
    );
    const { billing_address } = props.cartData;
    if (type === "error") return adminFormObject;
    if (type === "error" || !billing_address) return adminFormObject;
    const {
      firstname,
      lastname,
      company,
      city,
      street,
      region,
      country,
      postcode,
    } = billing_address;
    let { telephone } = billing_address;
    telephone = telephone?.replace("+1", "");
    const addressLine1 = street[0];
    const addressLine2 = street[1];
    const email = props.cartData.email;
    const jobTitle = props.cartData.custom_attributes.ln_job_title;
    return {
      firstname,
      lastname,
      email,
      telephone,
      jobTitle,
      company,
      addressLine1,
      addressLine2,
      city,
      region: region.region_id,
      country_code: country?.label,
      postcode,
    };
  };

  const setAdditionalUsersData = (type: string = "form") => {
    const {
      ln_fe,
      custom_attributes: { ln_sl_noattorney },
    } = props.cartData;
    if (ln_fe?.length === 0 || ln_sl_noattorney === 1) return [] as Array<LnFe>;
    const lnfe_list = [] as Array<LnFe>;
    let i = 0;
    while (i < ln_sl_noattorney - 1 && ln_fe && ln_fe[i]) {
      const { fe_firstname, fe_lastname, fe_email, fe_job_title } = ln_fe[i];
      if (type === "error")
        lnfe_list.push({
          fe_firstname: "",
          fe_lastname: "",
          fe_email: "",
          fe_job_title: "",
          fe_is_admin: false,
        } as LnFe);
      else
        lnfe_list.push({
          fe_firstname,
          fe_lastname,
          fe_email,
          fe_job_title,
          fe_is_admin: false,
        } as LnFe);
      i++;
    }
    return lnfe_list;
  };

  const location = useAppSelector(selectLocationSelector);
  const storeData = useAppSelector(selectStoreConfigSelector);

  const userInfoInitObject: LnFe =
    constants.formSection.additionalForm.form.reduce(
      (acc, key) => ({ ...acc, [key.id]: "", fe_is_admin: false }),
      {} as LnFe
    );

  const [adminForm, setAdminFormState] = useState(generateKeyFromCart());
  const [adminErrorFields, setAdminErrorFieldsState] = useState(
    generateKeyFromCart("error")
  );
  const [usersInfo, setUsersInfo] = useState(setAdditionalUsersData());
  const [usersErrorFields, setUsersErrorFieldState] = useState(
    setAdditionalUsersData("error")
  );
  const [selectedCountry, setSelectedCountry] = useState({
    value: "",
    label: "",
  } as { value: string; label: string });
  const [countries, setCountries] = useState(
    [] as Array<{ value: string; label: string }>
  );
  const [countryStates, setCountryStates] = useState(
    [] as Array<{ value: string; label: string }>
  );
  const [saveDataLoader, setSaveDataLoader] = useState(false);
  const [respError, setRespError] = useState([] as Array<string>);
  const [show, setShow] = useState(false);
  const [isAddressUpdated, setIsAddressUpdated] = useState(false);
  const [recommendedAddress, setRecommendedAddress] = useState({} as any);
  const { setCart } = cartActionDispatch(useAppDispatch());

  const validationRequired = props.requireValidation === "1" ? true : false;
  const { ln_sl_noattorney } = props.cartData.custom_attributes;
  const isUserInfoLengthValid =
    usersInfo.length + 1 === ln_sl_noattorney && ln_sl_noattorney > 1;
  const isAttorneyCountUnderLimit = ln_sl_noattorney < props.maxAttornies;
  let { email: adminEmail, addressLine1, addressLine2, postcode } = adminForm;
  let userEmailArray = usersInfo.map((user) => user.fe_email);

  const updateErrorFields = (errors: any) => {
    if (errors.adminEmailError || adminErrorFields.email) {
      setAdminErrorFieldsState((prevState: any) => ({
        ...prevState,
        email: errors.adminEmailError,
      }));
    }
    if (errors.userEmailErrors && errors.userEmailErrors.length > 0) {
      setUsersErrorFieldState((prevState) =>
        prevState.map((userError, idx) => ({
          ...userError,
          fe_email: errors.userEmailErrors[idx] || "",
        }))
      );
    }
  };

  const state =
    countryStates.find((s) => adminForm.region === s.value)?.label ||
    location.two_letter_abbreviation;
  const stateCode =
    location?.available_regions?.find((region) => region.name === state)
      ?.code || "";

  const addressValidation = async (): Promise<string | null> => {
    if (addressLine1 && postcode) {
      if (validationRequired) {
        setSaveDataLoader(true);
        const city = adminForm.city || location.full_name_locale;

        const country = location.full_name_english;
        const validationRes = await validateAddress(
          addressLine1 || "",
          addressLine2 || "",
          city || "",
          stateCode || "",
          country || "",
          postcode || "",
          props.addressValidationURL || ""
        );
        if (
          validationRes.message ===
            constants.addressValidation.invalidAddress.message ||
          validationRes.message ===
            constants.addressValidation.formatting.message
        ) {
          if (
            validationRes.message ===
            constants.addressValidation.invalidAddress.message
          ) {
            setAdminErrorFieldsState((prevState: any) => ({
              ...prevState,
              addressLine1: validationRes.message,
              addressLine2: validationRes.message,
              city: validationRes.message,
              region: validationRes.message,
              postcode: validationRes.message,
            }));
          }
          if (
            validationRes.message ===
            constants.addressValidation.formatting.message
          ) {
            setRecommendedAddress(validationRes.correctedAddress);
            setShow(true);
          }
          setSaveDataLoader(false);
        }
        setSaveDataLoader(false);
        return validationRes.message;
      }
      return "";
    }
    return "";
  };

  const updateAdminState = (
    id: string,
    value: string,
    required: boolean,
    pattern: string
  ) => {
    setAdminFormState((prevState: any) => ({ ...prevState, [id]: value }));
    let errCheck = fieldValidations(value, required, pattern);
    setAdminErrorFieldsState({ ...adminErrorFields, [id]: errCheck });
    if (id.includes("email") && errCheck === "") {
      const errors = checkForDuplicateEmails(adminEmail, userEmailArray);
      updateErrorFields(errors);
    }
  };

  const updateUserState = (
    id: string,
    value: string,
    required: boolean,
    pattern: string,
    index: number
  ) => {
    setUsersInfo((prevState) => {
      const updatedUsers = [...prevState];
      updatedUsers[index] = { ...updatedUsers[index], [id]: value } as LnFe;
      return updatedUsers;
    });
    let errCheck = fieldValidations(value, required, pattern);

    setUsersErrorFieldState((prevState) => {
      const updatedErrors = [...prevState];
      updatedErrors[index] = {
        ...updatedErrors[index],
        [id]: fieldValidations(value, required, pattern),
      } as LnFe;
      return updatedErrors;
    });
    if (id.includes("email") && errCheck === "") {
      const errors = checkForDuplicateEmails(adminEmail, userEmailArray);
      updateErrorFields(errors);
    }
  };

  const truncateValue = (value: string, maxLength: number) => {
    return value.length > maxLength ? value.slice(0, maxLength) : value;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string = "admin",
    index: number = 0
  ) => {
    if (!e?.target) {
      const { label } =
        (e as unknown as { value: string; label: string }) || {};
      if (label) {
        changeState(label);
      }
      return;
    }

    const { id, value, required, pattern, textContent } = e.target;

    if (id.includes("region")) changeState(textContent);
    else if (type === "admin") {
      updateAdminState(id, value, required, pattern);
      if (id === "email") {
        adminEmail = value;
      }
      updateAdminState(id, value, required, pattern);
      if (id === "postcode" && value.length > 5) {
        const newZipcode = truncateValue(value, 5);
        updateAdminState(id, newZipcode, required, pattern);
      }
    } else {
      if (id === "fe_email") {
        userEmailArray[index] = value;
      }
      updateUserState(id, value, required, pattern, index);
    }

    // if (id.includes("email")) {
    //   const errors = checkForDuplicateEmails(adminEmail, userEmailArray);
    //   updateErrorFields(errors);
    // }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let err = false;
    Object.keys(adminForm).map((key: string) => {
      let field = constants.formSection.formFields.find(
        (field) => field.id === key
      );
      if (adminForm[key]?.length == 0 && field?.isRequired) {
        setAdminErrorFieldsState((prevState: any) => ({
          ...prevState,
          [key]: "This field is required",
        }));
        err = true;
      }
    });

    if (validationRequired) {
      const emailDomainValidation = async () => {
        const result = await validateEmail(
          adminEmail,
          props.storeCode || "",
          props.addressValidationURL || ""
        );
        const errors = { adminEmailError: result };
        updateErrorFields(errors);
        if (!result) {
          const duplicateErrors = checkForDuplicateEmails(
            adminEmail,
            userEmailArray
          );
          updateErrorFields(duplicateErrors);
        }
        return result;
      };

      const emailValidationResult = await emailDomainValidation();

      if (emailValidationResult !== "" && emailValidationResult !== null) {
        err = true;
      }

      if (!isAddressUpdated) {
        const validationResult = await addressValidation();
        if (validationResult !== "" && validationResult !== null) {
          err = true;
        }
      }
    }

    let clone = [...usersErrorFields];
    usersInfo.map((user, index) => {
      Object.keys(user).map((key: string) => {
        let field = constants.formSection.additionalForm.form.find(
          (field) => field.id === key
        );
        if (
          user[key as keyof LnFe]?.toString().length == 0 &&
          field?.isRequired
        ) {
          clone[index] = {
            ...clone[index],
            [key]: "This field is required",
          } as LnFe;
          err = true;
        }
      });
    });
    setUsersErrorFieldState(clone);

    if (err) return;
    if (
      adminErrorFields &&
      Object.values(adminErrorFields).some((field) => field !== "")
    )
      return;
    if (
      usersErrorFields &&
      Object.values(usersErrorFields).some((field) =>
        Object.values(field).some((f) => f !== "" && f)
      )
    )
      return;

    setSaveDataLoader(true);
    const street =
      adminForm.addressLine1 && adminForm.addressLine2
        ? [adminForm.addressLine1, adminForm.addressLine2]
        : [adminForm.addressLine1];
    const telephone =
      selectedCountry.label.replace(/[()]/g, "") + adminForm.telephone;

    let params = Object.keys(adminForm)
      .filter((key) => key !== "addressLine1" && key !== "addressLine2")
      .reduce((obj: any, key) => {
        obj[key] = adminForm[key];
        return obj;
      }, {});
    params = {
      ...params,
      cartId: props.cartData.id,
      telephone,
      street,
      city: adminForm.city,
      region: adminForm.region,
      lnFe: usersInfo,
      country_code: selectedCountry.value,
    };

    cartService
      .setEmailOnCart({
        cart_id: props.cartData.id,
        email: adminForm.email.trim(),
      })
      .then((_) => {
        cartService
          .setBillingAddressOnCart(params)
          .then((res) => {
            setCart(res);
            addDataLayer(
              cartValueEventDL("add_shipping_info", res),
              "add_shipping_info"
            );
            setRespError([]);
            setSaveDataLoader(false);
            props.setAdminForm(false);
            window.scrollTo(0, 0);
          })
          .catch((err: Error) => {
            setRespError((prevState: Array<string>) => {
              if (prevState.includes(err.message)) return prevState;
              else return [...prevState, err.message];
            });
            addDataLayer({ error: err.message }, "save_user_info_error");
            setSaveDataLoader(false);
          });
      })
      .catch((err: Error) => {
        setRespError((prevState: Array<string>) => {
          if (prevState.includes(err.message)) return prevState;
          else return [...prevState, err.message];
        });
        setSaveDataLoader(false);
      });
  };

  const addNewUser = () => {
    setUsersInfo([...usersInfo, userInfoInitObject]);
  };

  const removeUser = (index: number) => {
    const clone = [...usersInfo];
    userEmailArray.splice(index, 1);

    const cloneErr = [...usersErrorFields];
    clone.splice(index, 1);
    cloneErr.splice(index, 1);
    setUsersInfo(clone);
    setUsersErrorFieldState(cloneErr);

    const errors = checkForDuplicateEmails(adminEmail, userEmailArray);
    updateErrorFields(errors);
  };

  const changeCountry = (countryCode: string) => {
    let ind = countries.findIndex((country) => countryCode === country.value);
    if (ind > -1) setSelectedCountry(countries[ind]);
  };

  const changeState = (state: string | null) => {
    let ind = countryStates.findIndex((s) => state === s.label);
    if (ind > -1) {
      setAdminFormState({ ...adminForm, region: countryStates[ind].value });
      setAdminErrorFieldsState((prevState: any) => ({
        ...prevState,
        region: "",
      }));
    }
  };

  const getOptions = (type: string) => {
    switch (type) {
      case "autocomplete":
        return countryStates;
      case "tel":
        return countries;
    }
  };

  const resetAddressErrorsAndHideModal = async () => {
    setAdminErrorFieldsState((prevState: any) => ({
      ...prevState,
      addressLine1: "",
      addressLine2: "",
      city: "",
      region: "",
      postcode: "",
    }));
    setShow(false);
  };

  const handleAcceptRecommendation = async () => {
    await resetAddressErrorsAndHideModal();

    const state =
      location?.available_regions?.find(
        (region) => region.code === recommendedAddress.state
      )?.name || "";
    const regionID = countryStates.find((s) => state === s.label)?.value || "";

    setAdminFormState((prevState: any) => ({
      ...prevState,
      addressLine1: recommendedAddress.address1,
      addressLine2: recommendedAddress.address2 ?? "",
      city: recommendedAddress.city,
      region: regionID,
      postcode: recommendedAddress.postalCode,
    }));
    setIsAddressUpdated(true);
  };

  const handleOriginalAddress = () => {
    resetAddressErrorsAndHideModal();
    setIsAddressUpdated(true);
  };

  useEffect(() => {
    if (isAddressUpdated) {
      // Create a synthetic event
      const syntheticEvent = new Event("submit", {
        bubbles: true,
        cancelable: true,
      }) as unknown as React.FormEvent<HTMLFormElement>;
      handleSubmit(syntheticEvent);
      setIsAddressUpdated(false);
    }
  }, [isAddressUpdated]);

  const AddressDisplay = ({
    title,
    address1,
    address2,
    city,
    region,
    postalCode,
  }: {
    title: string;
    address1: string;
    address2: string;
    city: string;
    region: string;
    postalCode: string;
  }) => (
    <div className="px-5 sm:px-10">
      <h5 className="text-base leading-5 font-bold mt-4">{title}</h5>
      <p className="text-base">{address1}</p>
      <p className="text-base">{address2}</p>
      <p className="text-base">{`${city}, ${region}, ${location.full_name_english}, ${postalCode}`}</p>
    </div>
  );

  useEffect(() => {
    if (location && location.id) {
      setCountries([
        { label: location.two_letter_abbreviation, value: "(+1)" },
      ]); // Need to check, add country code as well here once available from api.
      setCountryStates(
        location.available_regions.map((state) => ({
          value: state.id,
          label: state.name,
        }))
      );
      setSelectedCountry({
        value: location.two_letter_abbreviation,
        label: "(+1)",
      });
    }
    if (props.cartData && props.cartData.billing_address) {
      changeState(props.cartData.billing_address.region.label);
    }
  }, [location]);

const headingWithLink = constants.maxAttorneyAlert.heading.replace("{{link}}", process.env.REACT_APP_ROUTER_BASENAME || "");

const headingHtml = appendUtmParamsToLinks(headingWithLink);

  return (
    <div className="user-info-form">
      <div className="px-4">
        <h1 className="text-xl font-semibold mb-2">
          {constants.formSection.heading}
        </h1>
        <span className="text-grey-scale-slate text-sm mb-6">
          {constants.formSection.description}
        </span>
        <hr className="my-8 border-grey-scale-sleet hidden md:block" />
        <h2 className="text-xl font-semibold mb-2">
          {constants.formSection.formHeading}
        </h2>
        <span className="text-grey-scale-slate text-sm mb-6 italic">
          {constants.formSection.formDescription}
        </span>
      </div>

      {recommendedAddress && (
        <Dialog
          title={
            <div className="flex">
              {constants.addressValidation.formatting.alert.heading}
            </div>
          }
          submit={handleAcceptRecommendation}
          submitTitle={
            constants.addressValidation.formatting.alert.acceptButton
          }
          cancelTitle={
            constants.addressValidation.formatting.alert.rejectButton
          }
          showFooter={true}
          open={show}
          handleClose={resetAddressErrorsAndHideModal}
          handleCancel={handleOriginalAddress}
        >
          <p className="text-base leading-5">
            {constants.addressValidation.formatting.alert.description}
          </p>
          <AddressDisplay
            title={constants.addressValidation.formatting.alert.enteredAddress}
            address1={adminForm.addressLine1}
            address2={adminForm.addressLine2}
            city={adminForm.city}
            region={stateCode}
            postalCode={adminForm.postcode}
          />
          <AddressDisplay
            title={
              constants.addressValidation.formatting.alert.correctedAddress
            }
            address1={recommendedAddress.address1}
            address2={recommendedAddress.address2}
            city={recommendedAddress.city}
            region={recommendedAddress.state}
            postalCode={recommendedAddress.postalCode}
          />
        </Dialog>
      )}

      <div>
        {respError.length > 0 &&
          respError.map((error, index) => (
            <span
              key={`error-${index}`}
              className="text-primary-red text-sm px-4 italic"
            >
              {error}
            </span>
          ))}
        {constants.formSection.formFields.map(
          (field, index) =>
            index % 2 === 0 && (
              <div
                key={`user-admin-form-field-${field.id}-${index}`}
                className={
                  field.setFullWidth
                    ? ""
                    : `flex flex-col md:flex-row justify-between align-center my-6 ${field.id === "postcode" ? "w-full xl:w-1/4" : "w-100"}`
                }
              >
                <Field
                  {...field}
                  onChange={handleChange}
                  value={
                    adminForm[field.id as keyof typeof adminForm] as string
                  }
                  options={getOptions(field.type)}
                  selectedCountry={selectedCountry}
                  changeCountry={(countryCode: string) =>
                    changeCountry(countryCode)
                  }
                  errorMsg={
                    adminErrorFields[
                      field.id as keyof typeof adminErrorFields
                    ] as string
                  }
                />

                {index < constants.formSection.formFields.length - 1 && (
                  <Field
                    {...constants.formSection.formFields[index + 1]}
                    onChange={handleChange}
                    value={
                      adminForm[
                        constants.formSection.formFields[index + 1]
                          .id as keyof typeof adminForm
                      ] as string
                    }
                    options={getOptions(
                      constants.formSection.formFields[index + 1].type
                    )}
                    selectedCountry={selectedCountry}
                    changeCountry={(countryCode: string) =>
                      changeCountry(countryCode)
                    }
                    errorMsg={
                      adminErrorFields[
                        constants.formSection.formFields[index + 1]
                          .id as keyof typeof adminErrorFields
                      ] as string
                    }
                  />
                )}
              </div>
            )
        )}

        {validationRequired &&
          adminErrorFields.email === constants.emailValidation.message && (
            <StaticAlert
              type="error"
              desc={constants.emailValidation.description.replace(
                "{{contactUs}}",
                storeData.contact_us_url
              )}
            />
          )}
        {validationRequired &&
          adminErrorFields.addressLine1 ===
            constants.addressValidation.invalidAddress.message && (
            <StaticAlert
              type="error"
              desc={constants.addressValidation.invalidAddress.description.replace(
                "{{contactUs}}",
                storeData.contact_us_url
              )}
            />
          )}

        {props.cartData.custom_attributes.ln_sl_noattorney > 1 &&
          usersInfo.map((_, index) => (
            <div key={`user-info-${index}`} className="">
              <hr className="my-8 border-grey-scale-sleet hidden md:block" />
              <div className="my-4 flex align-center justify-between px-4">
                <h1 className="text-xl text-blues-ink font-semibold">
                  {constants.formSection.additionalForm.title} {index + 1}
                </h1>
                <button
                  data-track="remove-attorney"
                  className="text-accent-berry font-semibold"
                  type="button"
                  onClick={() => removeUser(index)}
                >
                  <img src={Trash} alt="trash" />
                </button>
              </div>
              <div className="flex flex-wrap">
                {constants.formSection.additionalForm.form.map(
                  (field, fIndex) => (
                    <div
                      key={`user-additional-form-field-${field.id}-${fIndex}`}
                      className={
                        field.setFullWidth
                          ? "my-2 w-full"
                          : `my-2 ${field.id === "postcode" ? "md:w-1/3" : "w-full md:w-1/2"}`
                      }
                    >
                      <Field
                        {...field}
                        onChange={(e) => handleChange(e, "user", index)}
                        value={
                          usersInfo[index]?.[field.id as keyof LnFe] as string
                        }
                        errorMsg={
                          usersErrorFields[index]?.[
                            field.id as keyof LnFe
                          ] as string
                        }
                        options={getOptions(field.type)}
                      />
                    </div>
                  )
                )}
              </div>
            </div>
          ))}
        {isUserInfoLengthValid && isAttorneyCountUnderLimit && (
          <StaticAlert
            type={constants.maxAttorneyAlert.type}
            heading={headingHtml}
            desc={constants.maxAttorneyAlert.description}
          />
        )}
        <div className="mt-6 md:mt-8 flex flex-col md:flex-row items-stretch md:items-center justify-end">
          <button
            data-track="submit-button"
            className="submit-btn !px-8 bg-blues-ink font-bold  lg:mr-4"
            disabled={saveDataLoader}
            onClick={handleSubmit}
          >
            {saveDataLoader ? (
              <div>
                <div
                  className="inline-block h-4 w-4 mr-2 mt-1 animate-spin rounded-full border-4 border-solid border-current border-white border-b-grey-scale-blue-grey-100 align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                >
                  {" "}
                </div>{" "}
                {constants.formSection.buttonLabels.loading}
              </div>
            ) : (
              <span>{constants.formSection.buttonLabels.submit}</span>
            )}
          </button>
          {usersInfo.length <
            props.cartData.custom_attributes.ln_sl_noattorney - 1 && (
            <Button
              submit={addNewUser}
              label={constants.formSection.buttonLabels.addUser}
              customClass="!text-blues-ink !bg-white mt-1 md:mx-1 md:mt-0"
            />
          )}
        </div>
        <span className="flex justify-end mt-4 text-sm text-grey-scale-slate">
          {constants.additionalUserSuggestion}
        </span>
      </div>
    </div>
  );
};

export default UserDetailsForm;
